import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { chatApi } from '@/lib/api/chat'
import { toApiError } from '@/lib/api/errors'
import { openTurnStream } from '@/lib/sse/eventStream'
import { queryKeys } from '@/lib/queryClient'
import { applyEvent, createTurn, restoreTurn } from './turnReducer'
import type { ChatTurn } from '@/types/events'

/**
 * Conversation state.
 *
 * Turns are UI state, not server cache, so they live in `useState` rather than TanStack
 * Query — they are appended to by a stream, not fetched and invalidated. The query client
 * is still used to invalidate the *observability* caches when a turn ends, so a tool call
 * made from chat shows up in Executions and Metrics without a manual refresh.
 *
 * Because that state is local, leaving the page destroys it — while the conversation itself
 * is not local at all: it is rows in PostgreSQL, owned by this user. So on mount this asks
 * the server which conversation it is in and rebuilds the thread from the stored transcript,
 * then attaches a live stream to anything still running. The server stays the source of
 * truth throughout; nothing is remembered across a navigation.
 */
export function useChat() {
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  // Set when the server refuses a turn for want of credits. Kept apart from the turns
  // themselves because no turn exists: the server rejected the request before creating one,
  // so there is nothing in the transcript to attach a failure to, and inventing a failed
  // turn would show something a reload makes disappear.
  const [creditsMessage, setCreditsMessage] = useState<string | null>(null)
  const closeStreamRef = useRef<(() => void) | null>(null)
  const queryClient = useQueryClient()

  // The list shows a thread's name and its position by activity, both of which change the
  // moment a turn lands — including the name, which is written from the first question.
  const refreshConversations = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.conversations })
  }, [queryClient])

  const patchTurn = useCallback((turnId: string, patch: (turn: ChatTurn) => ChatTurn) => {
    setTurns((current) => current.map((turn) => (turn.turnId === turnId ? patch(turn) : turn)))
  }, [])

  /**
   * Follow a turn's event stream.
   *
   * Shared by sending and recovering because they are the same act: the stream replays from
   * its first event, so a client that attaches late sees the whole turn, not just the rest
   * of it.
   */
  const attach = useCallback(
    (turnId: string) => {
      closeStreamRef.current?.()
      setIsStreaming(true)
      closeStreamRef.current = openTurnStream(chatApi.streamUrl(turnId), {
        onEvent: (event) => patchTurn(turnId, (turn) => applyEvent(turn, event)),
        onError: (message) =>
          patchTurn(turnId, (turn) =>
            turn.status === 'complete' ? turn : { ...turn, status: 'failed', errorMessage: message },
          ),
        onClose: () => {
          setIsStreaming(false)
          closeStreamRef.current = null
          // A chat turn produces real tool executions; the observability pages should
          // reflect them without the user reloading. This prefix also covers
          // ['executions', 'unseen'], so the sidebar badge moves the moment a tool call
          // finishes rather than on the next poll — which is the case that matters, since
          // the user is looking at Chat when it happens.
          void queryClient.invalidateQueries({ queryKey: ['executions'] })
          void queryClient.invalidateQueries({ queryKey: ['metrics'] })
          // A turn that ran spent credits. The number in the bar is stale the moment it
          // finishes, and it is the number someone watches when they are running low.
          void queryClient.invalidateQueries({ queryKey: queryKeys.credits })
          refreshConversations()
        },
      })
    },
    [patchTurn, queryClient, refreshConversations],
  )

  // Restore the conversation on every mount, which is what makes a remount, a navigation
  // and a refresh behave the same — from here they are one thing: a component with empty
  // state and a server that remembers.
  //
  // Two questions, in order. The transcript says what has been said; the active-turns list
  // says whether something is still running and needs a live stream. Asking only the second
  // was the bug: a finished conversation has no running turn, so returning to Chat restored
  // nothing at all.
  useEffect(() => {
    let cancelled = false

    void (async () => {
      let stored
      try {
        stored = await chatApi.currentConversation()
      } catch {
        // Recovery is an enhancement, not a precondition. A failure here must not stop
        // someone using chat, so it is swallowed rather than surfaced as a broken page.
        return
      }
      if (cancelled || !stored.conversation_id) return

      setConversationId(stored.conversation_id)
      setTurns((current) => {
        // Never clobber a turn already on screen: a mount that races a send would otherwise
        // replace the question the user just typed.
        const known = new Set(current.map((turn) => turn.turnId))
        const restored = stored.turns.filter((turn) => !known.has(turn.turn_id)).map(restoreTurn)
        return restored.length ? [...restored, ...current] : current
      })

      // Anything still running gets a live stream, so a turn in flight when the user
      // navigated away keeps arriving.
      let active
      try {
        active = await chatApi.activeTurns()
      } catch {
        return
      }
      if (cancelled) return

      const running = active.turns[active.turns.length - 1]
      if (running) {
        setTurns((current) =>
          current.some((turn) => turn.turnId === running.turn_id)
            ? current
            : [...current, createTurn(running.turn_id, running.question)],
        )
        attach(running.turn_id)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [attach])

  const send = useCallback(
    async (question: string) => {
      const trimmed = question.trim()
      if (!trimmed || isStreaming) return

      // The turn is rendered immediately with a provisional id so the question appears the
      // instant it is sent, rather than after a round trip.
      const provisionalId = `pending-${Date.now()}`
      setTurns((current) => [...current, createTurn(provisionalId, trimmed)])
      setIsStreaming(true)
      setCreditsMessage(null)

      let turnId: string
      try {
        const started = await chatApi.send(conversationId, trimmed)
        turnId = started.turn_id
        setConversationId(started.conversation_id)
        patchTurn(provisionalId, (turn) => ({ ...turn, turnId }))
      } catch (error) {
        const apiError = toApiError(error)
        setIsStreaming(false)
        if (apiError.errorType === 'INSUFFICIENT_CREDITS') {
          // The request never became a turn. Drop the optimistic one rather than leaving a
          // failed bubble in the thread, and say so once, above the composer — with the
          // question still in hand so it can be sent again after a top-up.
          setTurns((current) => current.filter((turn) => turn.turnId !== provisionalId))
          setCreditsMessage(apiError.message)
          void queryClient.invalidateQueries({ queryKey: queryKeys.credits })
          return
        }
        patchTurn(provisionalId, (turn) => ({
          ...turn,
          status: 'failed',
          errorMessage: apiError.message,
        }))
        return
      }

      attach(turnId)
    },
    [attach, conversationId, isStreaming, patchTurn, queryClient],
  )


  /**
   * Switch to another thread.
   *
   * The transcript is fetched rather than held: several conversations each keeping their
   * own turns in memory would be the same state duplicated, and the server is the one that
   * decides what is in a thread anyway. Telling it which thread is open is the other half —
   * that is what makes a later visit land back here instead of in the newest one.
   */
  const open = useCallback(
    async (id: string) => {
      if (id === conversationId) return
      closeStreamRef.current?.()
      closeStreamRef.current = null
      setIsStreaming(false)
      try {
        const opened = await chatApi.openConversation(id)
        setConversationId(opened.conversation_id)
        setTurns(opened.turns.map(restoreTurn))
      } catch {
        // Leave the current thread on screen. Clearing it would strand the user on a blank
        // page that still belongs to the conversation they were in.
      }
    },
    [conversationId],
  )

  const stop = useCallback(async () => {
    const streaming = turns.find((turn) => turn.status === 'streaming')
    closeStreamRef.current?.()
    closeStreamRef.current = null
    setIsStreaming(false)
    if (streaming) {
      try {
        await chatApi.cancel(streaming.turnId)
      } catch {
        // The stream is already closed locally; a failed cancel is not worth surfacing.
      }
      patchTurn(streaming.turnId, (turn) => ({ ...turn, status: 'complete' }))
    }
  }, [patchTurn, turns])

  /**
   * Start a new conversation, explicitly.
   *
   * The server is told, rather than the browser simply forgetting: clearing local state
   * alone would leave the old conversation as the one recovered on the next mount, so the
   * thread the user just left would come straight back. Nothing is deleted — the previous
   * conversation keeps its id and its turns.
   */
  const reset = useCallback(async () => {
    closeStreamRef.current?.()
    closeStreamRef.current = null
    setIsStreaming(false)
    try {
      const started = await chatApi.startConversation()
      setConversationId(started.conversation_id)
    } catch {
      // The server could not switch. Keep showing the thread rather than clearing to a
      // blank page that would silently reappend to the old conversation on the next send.
      return
    }
    setTurns([])
    refreshConversations()
  }, [refreshConversations])

  return { turns, conversationId, isStreaming, creditsMessage, send, stop, reset, open }
}
